import {
  Addon,
  cacheKeys,
  FeatureName,
  isAssetReadOnly,
  Media,
  useAllWorkspaceGroups,
  useCreateGroup,
  useCurrentAccount,
  useCurrentWorkspace,
  useIsFeatureEnabled,
  useProduct,
  useUpdateAsset,
  useWorkspaceAsset,
  WorkspaceType,
} from '@gripp/shared-logic';
import {
  AssetTagGallery,
  CategoryFilter,
  Colors,
  ControlledCheckboxItem,
  ControlledCheckboxItems,
  ControlledTextInput,
  DeleteAsset,
  GroupSelector,
  LoadingIndicator,
  PinnedActivities,
  SlideLayout,
  Spacing,
  usePinnedActivities,
  ViewMode,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { useNavigate, useParams } from 'react-router-dom';
import { array, boolean, string, object as yupObject } from 'yup';
import { AddFilesLinksButton } from './addFilesLinksButton';
import { AssetCard } from './assetCard';
import { AssetFileItem } from './assetFileItem';
import { AssetFilters } from './assetFilters';
import { EditAsset as EditAssetOld } from './edit-old';
import { useAssetTags } from './useAssetTags';
import { WebTagPicker } from './webTagPicker';

type MediaExtended = Media & { isFromProduct?: boolean };

export const EditAsset = () => {
  const isCropCircleEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  if (isCropCircleEnabled) {
    return <EditAssetBase />;
  }

  return <EditAssetOld />;
};

export const EditAssetBase = () => {
  const { assetId = '' } = useParams();
  const navigate = useNavigate();

  if (!assetId) {
    return null;
  }

  const leftColumn = {
    title: 'assets.menu.title',
    mainComponent: <AssetCard assetId={assetId} />,
    headerComponent: <AssetFilters assetId={assetId} />,
    showBackButton: true,
    onBackButtonPress: () => {
      navigate(`/assets/${assetId}`);
    },
  };

  const rightColumn = {
    title: 'assets.menu.title',
    mainComponent: <EditAssetComponent assetId={assetId} />,
  };

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      viewMode={ViewMode.DUAL}
    />
  );
};

export const EditAssetComponent = ({ assetId }: { assetId: string }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const workspace = useCurrentWorkspace(true);
  const isOEM = workspace?.type === WorkspaceType.Oem;
  const { updateAsset } = useUpdateAsset({ assetId: assetId! });
  const {
    workspaceAsset,
    isLoading: isLoadingWorkspaceAsset,
    invalidateQuery,
  } = useWorkspaceAsset(assetId!);
  const asset = workspaceAsset?.asset;
  const { account } = useCurrentAccount();
  const canEdit = !isAssetReadOnly(account, workspaceAsset);

  // State for category filtering
  const [selectedCategoryId, setSelectedCategoryId] = useState<
    string | undefined
  >(undefined);

  // Get workspace from workspaceAsset (more reliable for global admins)
  const workspaceId = workspaceAsset?.workspace?.id;
  const { groups } = useAllWorkspaceGroups(workspaceId);
  const { createGroup } = useCreateGroup();
  const { product, isLoading: isLoadingProduct } = useProduct(
    asset?.product?.id || ''
  );
  const { assetTags } = useAssetTags(assetId!);
  const { pinnedActivities } = usePinnedActivities(assetId!);
  const [showAllPinnedActivities, setShowAllPinnedActivities] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [leftHeight, setLeftHeight] = useState<number | null>(null);

  // Initialize selectedGroups when workspaceAsset changes
  useEffect(() => {
    if (workspaceAsset?.groups) {
      setSelectedGroups(
        workspaceAsset.groups.map((group) => ({
          id: group.id,
          name: group.name,
        }))
      );
    }
  }, [workspaceAsset?.groups]);

  const formSchema = yupObject({
    name: string().required(),
    description: string().optional(),
    details: string().optional(),
    tagIds: array().of(string()).optional().nullable(),
    groupIds: array().of(string()).optional().nullable(),
    addons: array().of(string()).optional().nullable(),
    product: string().optional().nullable(),
    serialNumber: string().optional().nullable(),
    hideReportAndNoteOptionsAnonymous: boolean().optional(),
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
    reset,
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: {
      name: asset?.name ?? '',
      description: asset?.description ?? '',
      details: asset?.details ?? '',
      tagIds: assetTags.map((tag) => tag.id),
      groupIds: workspaceAsset?.groups?.map((group) => group.id),
      addons: asset?.addons?.map((addon) => addon.id),
      product: asset?.product?.name ?? '',
      serialNumber: asset?.serialNumber ?? '',
      hideReportAndNoteOptionsAnonymous:
        workspaceAsset?.anonymousOptions?.hideReportAndNote ?? false,
    },
  });

  const addAssetTag = (tag: any) => {
    const tagIds = watch('tagIds') || [];
    setValue('tagIds', [...tagIds, tag.id], {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const deleteAssetTag = (tagId: any) => {
    const tagIds = watch('tagIds') || [];
    setValue(
      'tagIds',
      tagIds.filter((id) => id !== tagId),
      {
        shouldDirty: true,
        shouldValidate: true,
      }
    );
  };

  const onSubmit = async (data: any) => {
    const { product, ...submissionData } = data;
    const input: any = {
      ...submissionData,
      groupIds: selectedGroups.map((group) => group.id),
      id: assetId,
    };

    await (updateAsset as any)(
      { input: input },
      {
        onSettled: async () => {
          reset({}, { keepValues: true });
          await queryClient.invalidateQueries({
            queryKey: cacheKeys('workspaceAsset').all,
          });
        },
      }
    );
  };

  const allFiles = useMemo(() => {
    const files: MediaExtended[] = [];

    const productMediaIds = new Set(
      product?.media?.map((m: Media) => m.id) || []
    );

    // Add all asset files and mark which ones are from the product
    if (asset?.media) {
      asset.media.forEach((media: Media) => {
        const isFromProduct = productMediaIds.has(media.id);
        files.push({ ...media, isFromProduct });
      });
    }

    return files;
  }, [asset?.media, product?.media]);

  // Filter files based on selected category
  const filteredFiles = useMemo(() => {
    if (!selectedCategoryId) {
      return allFiles;
    }

    return allFiles.filter((file) =>
      file.categories?.some((category) => category.id === selectedCategoryId)
    );
  }, [allFiles, selectedCategoryId]);

  const renderFiles = () => {
    return (
      <View
        style={[
          styles.fileContainer,
          leftHeight ? { maxHeight: leftHeight - 180 } : null,
        ]}
      >
        <CategoryFilter
          allFiles={allFiles}
          selectedCategoryId={selectedCategoryId}
          onCategorySelect={setSelectedCategoryId}
        />
        <ScrollView showsVerticalScrollIndicator={true}>
          {filteredFiles.map((file: MediaExtended) => (
            <AssetFileItem
              key={file.id}
              asset={asset}
              media={file}
              invalidateQuery={invalidateQuery}
              isFromProduct={file.isFromProduct}
              disabled={!canEdit}
            />
          ))}
        </ScrollView>
      </View>
    );
  };

  if (isLoadingWorkspaceAsset || isLoadingProduct) {
    return <LoadingIndicator />;
  }

  const isEmptyAssetFiles = filteredFiles.length === 0;

  const renderColumnContainer = () => (
    <View style={styles.columnContainer}>
      <View style={styles.card}>
        <Text style={styles.cardHeader}>{t('asset.information')}</Text>
        {isOEM && (
          <View style={styles.formItem}>
            <Text style={styles.formItemTitle}>{t('asset.productType')}</Text>
            <ControlledTextInput
              control={control}
              name="product"
              editable={false}
              useCropCircle
            />
          </View>
        )}
        <View style={styles.formItem}>
          <Text style={styles.formItemTitle}>{t('asset.name')}</Text>
          <ControlledTextInput
            control={control}
            name="name"
            placeholder={t('asset.namePlaceholder')}
            editable={canEdit}
            useCropCircle
          />
        </View>
        {!isOEM && (
          <View style={styles.formItem}>
            <Text style={styles.formItemTitle}>{t('asset.description')}</Text>
            <ControlledTextInput
              control={control}
              name="description"
              placeholder={t('asset.descriptionPlaceholder')}
              editable={canEdit}
              useCropCircle
            />
          </View>
        )}
        {!isOEM && (
          <View style={styles.formItem}>
            <Text style={styles.formItemTitle}>{t('asset.details')}</Text>
            <ControlledTextInput
              control={control}
              placeholder={t('asset.detailsPlaceHolder')}
              name="details"
              editable={canEdit}
              textArea
              autoExpand
              useCropCircle
            />
          </View>
        )}
        {isOEM && (
          <View style={styles.formItem}>
            <Text style={styles.formItemTitle}>{t('asset.serialNumber')}</Text>
            <ControlledTextInput
              control={control}
              name="serialNumber"
              editable={canEdit}
              useCropCircle
            />
          </View>
        )}
      </View>

      {/* Pinned Activities Section */}
      <PinnedActivities
        assetId={assetId!}
        pinnedActivities={pinnedActivities}
        showAll={showAllPinnedActivities}
        onViewAllPress={() => setShowAllPinnedActivities(true)}
        onActivityPress={(activityId) => {
          // Navigate to activity page
          navigate(`/assets/${assetId}/activities/${activityId}`);
        }}
      />

      <View style={styles.card}>
        <View style={styles.formItem}>
          <View>
            <Text style={styles.formItemTitle}>{t('asset.groups')}</Text>
          </View>
          <GroupSelector
            selectedGroups={selectedGroups}
            availableGroups={groups}
            onGroupsChange={(groups) => {
              setSelectedGroups(groups);
              setValue(
                'groupIds',
                groups.map((g) => g.id),
                { shouldDirty: true, shouldValidate: true }
              );
            }}
            onCreateGroup={createGroup}
            disabled={!canEdit}
            useCropCircle
          />
        </View>
      </View>
      {product && product.addons && product.addons.length > 0 && (
        <View style={styles.card}>
          <View style={styles.formItem}>
            <View>
              <Text style={styles.formItemTitle}>
                {t('asset.addons', { count: asset?.addons?.length || 0 })}
              </Text>
            </View>
            <View style={styles.addonsContainer}>
              <ControlledCheckboxItems
                control={control as any}
                name="addons"
                options={product.addons
                  .slice()
                  .sort((a: Addon, b: Addon) => a.name.localeCompare(b.name))
                  .map((addon: Addon) => ({
                    value: addon.id,
                    label: addon.name,
                  }))}
                style={styles.checkboxItems}
                labelStyle={styles.checkboxItemsLabel}
                position="leading"
                disabled={!canEdit}
              />
            </View>
          </View>
        </View>
      )}
      <View style={styles.card}>
        <View style={styles.formItem}>
          <View>
            <Text style={styles.formItemTitle}>{t('asset.tagCode')}</Text>
          </View>
          <AssetTagGallery
            existingAssetTags={assetTags}
            onAssetTagAdded={addAssetTag}
            onAssetTagDeleted={deleteAssetTag}
            tagPicker={WebTagPicker}
            editable={canEdit}
          />
        </View>
      </View>

      {/* Files Section */}
      <View style={styles.card}>
        <Text style={styles.cardHeader}>{t('asset.files.title')}</Text>
        <View style={isEmptyAssetFiles && styles.marginTop}>
          {isEmptyAssetFiles ? (
            <Text style={styles.formItemTitle}>{t('asset.files.noFiles')}</Text>
          ) : (
            <View style={styles.fileContainer}>
              <CategoryFilter
                allFiles={allFiles}
                selectedCategoryId={selectedCategoryId}
                onCategorySelect={setSelectedCategoryId}
              />
              <ScrollView showsVerticalScrollIndicator={true}>
                {filteredFiles.map((file: MediaExtended) => (
                  <AssetFileItem
                    key={file.id}
                    asset={asset}
                    media={file}
                    invalidateQuery={invalidateQuery}
                    isFromProduct={file.isFromProduct}
                    disabled={!canEdit}
                  />
                ))}
              </ScrollView>
            </View>
          )}
          {asset && (
            <AddFilesLinksButton
              asset={asset}
              invalidateQuery={invalidateQuery}
              disabled={!canEdit}
            />
          )}
        </View>
      </View>

      <View style={styles.card}>
        <ControlledCheckboxItem
          control={control}
          label={t('asset.hideReportAndNoteOptionsAnonymous')}
          name="hideReportAndNoteOptionsAnonymous"
          position="leading"
          style={styles.checkboxItem}
          labelStyle={styles.hideReportLabel}
          mode="android"
          disabled={!canEdit}
        />
      </View>
    </View>
  );

  const canSave = isDirty && isValid;

  if (!asset) {
    return <LoadingIndicator />;
  }

  return (
    <>
      <ScrollView
        contentContainerStyle={styles.scrollView}
        showsVerticalScrollIndicator={false}
        showsHorizontalScrollIndicator={false}
      >
        <View style={styles.container}>{renderColumnContainer()}</View>
      </ScrollView>
      <View style={styles.footer}>
        {workspaceAsset && (
          <DeleteAsset
            workspaceAsset={workspaceAsset}
            onDeleted={async () => {
              navigate('/assets');
            }}
          />
        )}
        <View style={styles.footerActions}>
          <Text style={styles.cancelButton} onPress={() => navigate('/assets')}>
            {t('asset.cancel')}
          </Text>
          <Text
            style={[styles.saveButton, !canSave && styles.saveButtonDisabled]}
            onPress={canSave ? handleSubmit(onSubmit) : undefined}
            disabled={!canEdit}
          >
            {t('asset.save')}
          </Text>
        </View>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  addonsContainer: {
    width: 'auto',
    flexDirection: 'column',
    gap: 0,
    margin: 0,
    padding: 0,
  },
  fileContainer: {
    flexDirection: 'column',
    gap: 10,
  },
  marginTop: {
    marginTop: 150,
  },
  scrollView: {
    flexGrow: 1,
    paddingVertical: Spacing.basePadding.paddingVertical,
    paddingBottom: 120,
  },
  container: {
    flexDirection: 'row',
    gap: 20,
  },
  column: {
    maxWidth: 800,
    marginHorizontal: 'auto',
    width: '100%',
  },
  columnContainer: {
    width: '100%',
    gap: 20,
  },
  card: {
    backgroundColor: Colors.white,
    padding: 20,
    borderRadius: 8,
    gap: 20,
  },
  cardHeader: {
    fontSize: 20,
    fontWeight: 'bold',
    color: Colors.black,
  },
  formItem: {
    marginBottom: 10,
  },
  formItemTitle: {
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 16,
    marginBottom: 10,
  },
  selectionsDropdown: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: Colors.tabInactive,
    borderRadius: 8,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  footer: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    paddingHorizontal: 40,
    paddingVertical: 20,
    backgroundColor: Colors.white,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  footerActions: {
    flexDirection: 'row',
    gap: 16,
    marginLeft: 'auto',
  },
  cancelButton: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.orangeDark,
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: Colors.white,
    lineHeight: 16,
    textAlign: 'center',
  },
  saveButton: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.white,
    paddingHorizontal: 28,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: Colors.primary,
    lineHeight: 16,
    textAlign: 'center',
  },
  saveButtonDisabled: {
    backgroundColor: Colors.grayDivider,
    color: Colors.white,
  },
  checkboxItems: {
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  checkboxItem: {
    gap: 8,
    paddingLeft: 0,
  },
  hideReportLabel: {
    paddingLeft: 0,
    textAlign: 'left',
    fontSize: 16,
    lineHeight: 19,
    color: Colors.black,
    fontWeight: '400',
  },
  checkboxItemsLabel: {
    fontSize: 16,
    lineHeight: 19,
    color: Colors.black,
    fontWeight: '400',
    textAlign: 'left',
  },
});
